import { WarningCircleIcon } from '@phosphor-icons/react';
import * as Sentry from '@sentry/react';
import { ReactNode } from 'react';
import { store } from 'redux/store';
import { openSupportEmail } from 'utils/contactSupport';
import { USER_ROLES } from 'utils/roles/types';
import s from './ErrorBoundary.module.scss';

interface IErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

const isDeveloper = (): boolean =>
  import.meta.env.DEV || (store.getState().authSlice.role?.includes(USER_ROLES.DEVELOPER) ?? false);

export const ErrorBoundary = ({ children, fallback }: IErrorBoundaryProps) => {
  return (
    <Sentry.ErrorBoundary
      beforeCapture={scope => scope.setTag('boundary', 'app-root')}
      fallback={({ error, eventId }) => {
        if (fallback) {
          return <>{fallback}</>;
        }

        return (
          <div className={s.errorBoundary}>
            <div className={s.errorContent}>
              <div className={s.errorIcon}>
                <WarningCircleIcon size={64} />
              </div>
              <h1 className={s.errorTitle}>Something went wrong</h1>
              <p className={s.errorMessage}>We&apos;ve encountered an unexpected error. Our team has been notified.</p>
              {isDeveloper() && error instanceof Error && (
                <details className={s.errorDetails}>
                  <summary>Error details (developer)</summary>
                  <pre className={s.errorStack}>{error.toString()}</pre>
                  {error.stack && <pre className={s.errorStack}>{error.stack}</pre>}
                </details>
              )}
              <div className={s.errorActions}>
                <button className={s.primaryButton} onClick={() => window.location.reload()}>
                  Reload Application
                </button>
                <button className={s.secondaryButton} onClick={() => openSupportEmail({ eventId })}>
                  Contact Support
                </button>
              </div>
            </div>
          </div>
        );
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};
